import { useState } from 'react'
import { Box, Chip, TableCell, TableRow, Typography } from '@mui/material'
import { IconPackage } from '@tabler/icons-react'
import { Product } from '../../types'
import ProductActionsMenu from '../components/ProductActionsMenu'
import DeleteProductDialog from '../components/DeleteProductDialog'
import SupplierLogo, { getSupplierLabel } from '../../../../components/SupplierLogo'

interface ProductRowProps {
  product: Product
  onEdit: (product: Product) => void
  onDelete: (product: Product) => void
}

const getBundleSuppliers = (bundleItems: Product[]): string[] => {
  const suppliers = bundleItems
    .map((item) => item.supplier)
    .filter((s): s is string => s !== null && s !== undefined)
  return [...new Set(suppliers)]
}

const ProductRow = ({ product, onEdit, onDelete }: ProductRowProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const price = parseFloat(product.price)
  const formattedPrice = Number.isNaN(price)
    ? '—'
    : `${price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`

  const formattedCondition =
    product.condition == null
      ? '—'
      : product.pricingType === 'percentage'
      ? `${product.condition}%`
      : `${product.condition.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€`

  const bundleSuppliers = product.isBundle ? getBundleSuppliers(product.bundleItems) : []
  const displaySuppliers = bundleSuppliers.slice(0, 2)
  const hasMoreSuppliers = bundleSuppliers.length > 2

  return (
    <TableRow hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          #{product.displayId}
        </Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {product.isBundle ? (
            <IconPackage size={20} color="#666" />
          ) : (
            <SupplierLogo supplier={product.supplier} showLabel={false} />
          )}
          <Typography variant="body2" fontWeight={500}>
            {product.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {product.isBundle
            ? displaySuppliers.length > 0
              ? displaySuppliers.map(getSupplierLabel).join(', ') + (hasMoreSuppliers ? ' ...' : '')
              : '—'
            : product.supplier
            ? getSupplierLabel(product.supplier)
            : '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{formattedPrice}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {product.runtime != null ? `${product.runtime} Days` : '—'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{formattedCondition}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={product.status === 'active' ? 'Active' : 'Inactive'}
          size="small"
          sx={{
            bgcolor: product.status === 'active' ? 'success.light' : 'grey.300',
            color: product.status === 'active' ? 'success.dark' : 'text.secondary',
            fontWeight: 500,
            borderRadius: '16px',
          }}
        />
      </TableCell>
      <TableCell width={75} align="center">
        <ProductActionsMenu
          onEdit={() => onEdit(product)}
          onDeleteClick={() => setDeleteDialogOpen(true)}
        />
      </TableCell>

      <DeleteProductDialog
        open={deleteDialogOpen}
        productName={product.name}
        onConfirm={() => {
          setDeleteDialogOpen(false)
          onDelete(product)
        }}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </TableRow>
  )
}

export default ProductRow
